/**
 * Admin Dashboard - Metrics Overview
 *
 * Displays high-level platform metrics for super admins:
 * - Tenant stats (total, active, disabled)
 * - Lead stats (total, verified, this month)
 * - Generation stats (total, this month)
 * - Revenue metrics (paying tenants, estimated MRR)
 * - Signup trend (last 6 months)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Users, ImageIcon, DollarSign, TrendingUp, AlertCircle, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAdminDashboard } from '@/hooks/api/useAdminApi';

export default function AdminDashboard() {
  const { data, isLoading, error } = useAdminDashboard();

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard metrics: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Tenants Section */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Tenants</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <MetricCard
            title="Total Tenants"
            value={data?.tenants.total}
            icon={Building2}
            isLoading={isLoading}
          />
          <MetricCard
            title="Active"
            value={data?.tenants.active}
            icon={ShieldCheck}
            isLoading={isLoading}
          />
          <MetricCard
            title="Disabled"
            value={data?.tenants.disabled}
            icon={ShieldOff}
            isLoading={isLoading}
          />
        </div>
      </section>

      {/* Leads Section */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Leads</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <MetricCard
            title="Total Leads"
            value={data?.leads.total}
            icon={Users}
            isLoading={isLoading}
          />
          <MetricCard
            title="Verified"
            value={data?.leads.verified}
            icon={ShieldCheck}
            isLoading={isLoading}
          />
          <MetricCard
            title="This Month"
            value={data?.leads.thisMonth}
            icon={TrendingUp}
            isLoading={isLoading}
          />
        </div>
      </section>

      {/* Generations Section */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Generations</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <MetricCard
            title="Total Generations"
            value={data?.generations.total}
            icon={ImageIcon}
            isLoading={isLoading}
          />
          <MetricCard
            title="This Month"
            value={data?.generations.thisMonth}
            icon={TrendingUp}
            isLoading={isLoading}
          />
        </div>
      </section>

      {/* Revenue Section */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Revenue</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <MetricCard
            title="Paying Tenants"
            value={data?.revenue.payingTenants}
            icon={DollarSign}
            isLoading={isLoading}
          />
          <MetricCard
            title="Estimated MRR"
            value={data?.revenue.estimatedMrr}
            icon={DollarSign}
            isLoading={isLoading}
            formatter={(val) => `$${val.toLocaleString()}`}
          />
        </div>
      </section>

      {/* Signup Trend Section */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Signup Trend (Last 6 Months)</h2>
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.signupTrend && data.signupTrend.length > 0 ? (
                    data.signupTrend.map((row) => (
                      <TableRow key={row.month}>
                        <TableCell className="font-medium">{row.month}</TableCell>
                        <TableCell className="text-right">{row.count}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-muted-foreground">
                        No signup data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

// ============================================
// Components
// ============================================

interface MetricCardProps {
  title: string;
  value?: number;
  icon: React.ComponentType<{ className?: string }>;
  isLoading: boolean;
  formatter?: (value: number) => string;
}

function MetricCard({ title, value, icon: Icon, isLoading, formatter }: MetricCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div className="text-2xl font-bold">
            {value !== undefined ? (formatter ? formatter(value) : value.toLocaleString()) : '—'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
